import { Link } from 'react-router-dom';
import { Flame, Trophy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from './EmptyState';
import type { OutletBreakdown, ProviderSnapshot } from '@/hooks/useOutletProviderBreakdown';
import { useOutlets } from '@/hooks/useOutlets';
import { formatINRCompact, formatNumberCompact } from '@/lib/format';
import { cn } from '@/lib/utils';

interface Props {
  outlets: OutletBreakdown[];
  loading?: boolean;
}

type WhichWins = 'swiggy' | 'zomato' | 'tie';

interface MetricSpec {
  key: keyof ProviderSnapshot;
  label: string;
  format: (n: number) => string;
  /** Lower-is-better metrics (e.g. poor-rated %). */
  invert?: boolean;
  /** Hide if both are 0. */
  hideZero?: boolean;
  /** Threshold above which a cell should be flagged with color. */
  threshold?: { value: number; tone: 'rose' | 'amber'; when: 'above' | 'below' };
}

const METRICS: MetricSpec[] = [
  { key: 'netSales', label: 'Net sales', format: formatINRCompact },
  { key: 'deliveredOrders', label: 'Delivered', format: formatNumberCompact },
  { key: 'aov', label: 'AOV', format: formatINRCompact },
  { key: 'impressions', label: 'Impressions', format: formatNumberCompact, hideZero: true },
  {
    key: 'funnelConvPct',
    label: 'Funnel conv',
    format: (n) => `${n.toFixed(1)}%`,
    hideZero: true,
    threshold: { value: 0.7, tone: 'rose', when: 'below' },
  },
  {
    key: 'poorRatedPct',
    label: 'Poor-rated',
    format: (n) => `${n.toFixed(1)}%`,
    invert: true,
    threshold: { value: 20, tone: 'rose', when: 'above' },
  },
];

function winnerOf(s: number, z: number, invert?: boolean): WhichWins {
  if (s === 0 && z === 0) return 'tie';
  if (s === 0) return 'zomato';
  if (z === 0) return 'swiggy';
  const swiggyBetter = invert ? s < z : s > z;
  const gap = Math.abs(s - z) / Math.max(s, z);
  if (gap < 0.02) return 'tie';
  return swiggyBetter ? 'swiggy' : 'zomato';
}

function gapPct(s: number, z: number): number {
  const max = Math.max(s, z);
  const min = Math.min(s, z);
  return min > 0 ? ((max - min) / min) * 100 : 100;
}

export function HeadToHead({ outlets, loading }: Props) {
  const { data: outletList = [] } = useOutlets();
  const outletName = (id: string) => outletList.find((o) => o.id === id)?.name ?? id;

  if (loading) {
    return (
      <div className="space-y-2 p-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }
  if (outlets.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          title="No outlet data"
          description="No aggregator rows found for any outlet in this period."
        />
      </div>
    );
  }

  // Aggregate scorecard — sum across outlets.
  const totalSwiggy = outlets.reduce(
    (acc, o) => ({
      netSales: acc.netSales + o.swiggy.netSales,
      deliveredOrders: acc.deliveredOrders + o.swiggy.deliveredOrders,
    }),
    { netSales: 0, deliveredOrders: 0 },
  );
  const totalZomato = outlets.reduce(
    (acc, o) => ({
      netSales: acc.netSales + o.zomato.netSales,
      deliveredOrders: acc.deliveredOrders + o.zomato.deliveredOrders,
    }),
    { netSales: 0, deliveredOrders: 0 },
  );
  const totalSalesGapPct = gapPct(totalSwiggy.netSales, totalZomato.netSales);
  const totalSalesWinner = winnerOf(totalSwiggy.netSales, totalZomato.netSales);

  // ── Insight chips computed from the data ──────────────────────────────
  const swiggyLeads = outlets.filter(
    (o) => winnerOf(o.swiggy.netSales, o.zomato.netSales) === 'swiggy',
  ).length;
  const zomatoLeads = outlets.filter(
    (o) => winnerOf(o.swiggy.netSales, o.zomato.netSales) === 'zomato',
  ).length;

  // Biggest lead per platform on net sales
  const withLeadSize = outlets.map((o) => ({
    o,
    w: winnerOf(o.swiggy.netSales, o.zomato.netSales),
    gap: gapPct(o.swiggy.netSales, o.zomato.netSales),
  }));
  const biggestSwiggyLead = [...withLeadSize]
    .filter((x) => x.w === 'swiggy')
    .sort((a, b) => b.gap - a.gap)[0];
  const biggestZomatoLead = [...withLeadSize]
    .filter((x) => x.w === 'zomato')
    .sort((a, b) => b.gap - a.gap)[0];

  // Worst AOV gap (any direction)
  const aovGaps = outlets
    .filter((o) => o.swiggy.aov > 0 && o.zomato.aov > 0)
    .map((o) => ({ o, gap: gapPct(o.swiggy.aov, o.zomato.aov) }))
    .sort((a, b) => b.gap - a.gap);
  const worstAovGap = aovGaps[0];

  // Worst poor-rated (any platform)
  const worstPoor = outlets
    .map((o) => {
      const worst = Math.max(o.swiggy.poorRatedPct, o.zomato.poorRatedPct);
      const which =
        o.swiggy.poorRatedPct >= o.zomato.poorRatedPct ? 'Swiggy' : 'Zomato';
      return { o, worst, which };
    })
    .filter((x) => x.worst > 0)
    .sort((a, b) => b.worst - a.worst)[0];

  return (
    <div className="space-y-6">
      <Scorecard
        swiggy={totalSwiggy.netSales}
        zomato={totalZomato.netSales}
        swiggyOrders={totalSwiggy.deliveredOrders}
        zomatoOrders={totalZomato.deliveredOrders}
        gap={totalSalesGapPct}
        winner={totalSalesWinner}
      />

      {/* Insight strip — auto-computed from the data */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border/40 bg-muted/20 px-3 py-2 text-2xs">
        <InsightChip
          label="Swiggy leads"
          value={`${swiggyLeads} outlet${swiggyLeads === 1 ? '' : 's'}`}
          tone="orange"
        />
        <InsightChip
          label="Zomato leads"
          value={`${zomatoLeads} outlet${zomatoLeads === 1 ? '' : 's'}`}
          tone="rose"
        />
        {biggestSwiggyLead && (
          <InsightChip
            label="Biggest Swiggy lead"
            value={`${outletName(biggestSwiggyLead.o.outletId)} +${biggestSwiggyLead.gap.toFixed(0)}%`}
            tone="orange"
          />
        )}
        {biggestZomatoLead && (
          <InsightChip
            label="Biggest Zomato lead"
            value={`${outletName(biggestZomatoLead.o.outletId)} +${biggestZomatoLead.gap.toFixed(0)}%`}
            tone="rose"
          />
        )}
        {worstAovGap && worstAovGap.gap > 20 && (
          <InsightChip
            label="Largest AOV gap"
            value={`${outletName(worstAovGap.o.outletId)} ${worstAovGap.gap.toFixed(0)}%`}
            tone="amber"
          />
        )}
        {worstPoor && worstPoor.worst > 20 && (
          <InsightChip
            icon={<Flame className="h-3 w-3" />}
            label="Worst poor-rated"
            value={`${outletName(worstPoor.o.outletId)} ${worstPoor.worst.toFixed(0)}% (${worstPoor.which})`}
            tone="rose"
          />
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border/60 bg-muted/30 text-2xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Outlet</th>
              {METRICS.map((m) => (
                <th key={m.key} className="px-3 py-2 text-right font-medium" colSpan={3}>
                  {m.label}
                </th>
              ))}
            </tr>
            <tr className="text-[10px] normal-case">
              <th />
              {METRICS.map((m) => (
                <SubHeaderTriple key={m.key} />
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40">
            {outlets.map((o) => (
              <Row key={o.outletId} row={o} outletName={outletName(o.outletId)} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InsightChip({
  icon,
  label,
  value,
  tone,
}: {
  icon?: React.ReactNode;
  label: string;
  value: string;
  tone: 'orange' | 'rose' | 'amber';
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-border/40 bg-card px-2 py-1">
      <span
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          tone === 'orange' && 'bg-orange-500',
          tone === 'rose' && 'bg-rose-500',
          tone === 'amber' && 'bg-amber-500',
        )}
      />
      {icon}
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-semibold text-foreground">{value}</span>
    </span>
  );
}

function SubHeaderTriple() {
  return (
    <>
      <th className="px-2 py-1 text-right font-normal">Swiggy</th>
      <th className="px-2 py-1 text-right font-normal">Zomato</th>
      <th className="px-2 py-1 text-right font-normal text-muted-foreground">Gap</th>
    </>
  );
}

function Scorecard({
  swiggy,
  zomato,
  swiggyOrders,
  zomatoOrders,
  gap,
  winner,
}: {
  swiggy: number;
  zomato: number;
  swiggyOrders: number;
  zomatoOrders: number;
  gap: number;
  winner: WhichWins;
}) {
  const total = swiggy + zomato;
  const swiggyShare = total > 0 ? (swiggy / total) * 100 : 50;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between gap-3 text-sm">
          <span className="text-muted-foreground">All outlets · net sales</span>
          {winner !== 'tie' && (
            <Badge variant={winner === 'swiggy' ? 'positive' : 'negative'}>
              <Trophy className="mr-1 h-3 w-3" />
              {winner === 'swiggy' ? 'Swiggy' : 'Zomato'} leads · {gap.toFixed(1)}%
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <PlatformBlock
            name="Swiggy"
            sales={swiggy}
            orders={swiggyOrders}
            share={swiggyShare}
            color="orange"
          />
          <PlatformBlock
            name="Zomato"
            sales={zomato}
            orders={zomatoOrders}
            share={100 - swiggyShare}
            color="rose"
          />
        </div>
        <div className="flex h-2 w-full overflow-hidden rounded-md">
          <div className="h-full bg-orange-500" style={{ width: `${swiggyShare}%` }} />
          <div className="h-full bg-rose-500" style={{ width: `${100 - swiggyShare}%` }} />
        </div>
      </CardContent>
    </Card>
  );
}

function PlatformBlock({
  name,
  sales,
  orders,
  share,
  color,
}: {
  name: string;
  sales: number;
  orders: number;
  share: number;
  color: 'orange' | 'rose';
}) {
  return (
    <div className="rounded-md border border-border/40 p-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span
          className={cn('h-2 w-2 rounded-full', color === 'orange' ? 'bg-orange-500' : 'bg-rose-500')}
        />
        {name}
        <span className="ml-auto font-mono">{share.toFixed(0)}%</span>
      </div>
      <div className="mt-2 font-semibold tabular-nums text-2xl">{formatINRCompact(sales)}</div>
      <div className="mt-1 text-2xs text-muted-foreground">
        {formatNumberCompact(orders)} delivered orders
      </div>
    </div>
  );
}

function Row({ row, outletName }: { row: OutletBreakdown; outletName: string }) {
  return (
    <tr className="hover:bg-muted/30">
      <td className="px-3 py-2">
        <Link to={`/outlets/${row.outletId}`} className="font-medium hover:underline">
          {outletName}
        </Link>
      </td>
      {METRICS.map((m) => {
        const s = Number(row.swiggy[m.key] ?? 0);
        const z = Number(row.zomato[m.key] ?? 0);
        if (m.hideZero && s === 0 && z === 0) {
          return (
            <td key={m.key} colSpan={3} className="px-2 py-2 text-center text-2xs text-muted-foreground">
              —
            </td>
          );
        }
        const w = winnerOf(s, z, m.invert);
        const gp = gapPct(s, z);
        return (
          <Cell
            key={m.key}
            swiggy={s}
            zomato={z}
            winner={w}
            gap={gp}
            format={m.format}
            threshold={m.threshold}
          />
        );
      })}
    </tr>
  );
}

function Cell({
  swiggy,
  zomato,
  winner,
  gap,
  format,
  threshold,
}: {
  swiggy: number;
  zomato: number;
  winner: WhichWins;
  gap: number;
  format: (n: number) => string;
  threshold?: MetricSpec['threshold'];
}) {
  const flagged = (val: number): 'rose' | 'amber' | null => {
    if (!threshold || val === 0) return null;
    const hit = threshold.when === 'above' ? val >= threshold.value : val <= threshold.value;
    return hit ? threshold.tone : null;
  };
  const swiggyFlag = flagged(swiggy);
  const zomatoFlag = flagged(zomato);
  return (
    <>
      <td
        className={cn(
          'px-2 py-2 text-right font-mono tabular-nums',
          winner === 'swiggy' && 'font-semibold',
          swiggyFlag === 'rose' && 'text-rose-600 dark:text-rose-400',
          swiggyFlag === 'amber' && 'text-amber-600 dark:text-amber-400',
        )}
      >
        {format(swiggy)}
      </td>
      <td
        className={cn(
          'px-2 py-2 text-right font-mono tabular-nums',
          winner === 'zomato' && 'font-semibold',
          zomatoFlag === 'rose' && 'text-rose-600 dark:text-rose-400',
          zomatoFlag === 'amber' && 'text-amber-600 dark:text-amber-400',
        )}
      >
        {format(zomato)}
      </td>
      <td className="px-2 py-2 text-right text-2xs text-muted-foreground">
        {winner === 'tie' ? '—' : `${gap.toFixed(0)}%`}
      </td>
    </>
  );
}
